/**
 * Pure geometry for COMBAT.app's **draw** annotations — the visual-only polyline/freehand
 * layer everyone (GM and players alike) can mark the board with. Mirrors the split
 * {@link combatAuthoring} established for terrain: {@link CombatStage} owns the pointer wiring
 * + dispatch; everything *decided* (how a freehand drag samples down to a manageable point
 * list, when a stroke is worth committing, which `add_annotation` command a finished gesture
 * issues) lives here so it is unit-tested without a canvas.
 *
 * **Two brushes, two pipelines, one command shape:**
 *   - **Polyline** — the GM/player clicks vertices deliberately; they are stored **verbatim**,
 *     no simplification (deliberate placement — simplifying would second-guess the click).
 *   - **Freehand** — a drag samples the pointer at a **distance threshold**
 *     ({@link shouldSampleFreehandPoint}, ~10px = `CELL`/4, so a slow drag doesn't oversample
 *     and a fast one doesn't gap) and then, on release, the whole sampled path runs through
 *     **Ramer-Douglas-Peucker** ({@link simplifyPolyline}, epsilon {@link RDP_EPSILON} ≈ 2px)
 *     before it ever reaches the command bus. This is load-bearing, not cosmetic:
 *     `EncounterState` broadcasts **whole-state LWW** on every command, so an unsimplified
 *     freehand stroke would inflate every subsequent broadcast, not just its own — and the
 *     reducer's {@link MAX_ANNOTATION_POINTS} cap is the backstop, not the plan (a real stroke
 *     should land under it with room to spare; see the pathological-stroke test in
 *     `combatDrawing.test.ts`).
 *
 * Both pipelines terminate in an {@link AddAnnotationCommand} (or `null` for a degenerate
 * stroke — fewer than 2 points draws nothing) with `annotation.authorSlotId` stamped to the
 * **caller's own** `actorId` — the exact forge-proof shape `canIssueEncounter`'s `add_annotation`
 * gate checks against the server-stamped actor, so the client never has to (and never should)
 * invent a different owner.
 */
import type { AddAnnotationCommand, Annotation, AnnotationColor, AnnotationId, EncounterBatchCommand, RemoveAnnotationCommand, SetAnnotationCommand } from "@lancer-vtt/domain";
import { CELL } from "./combatCells";

/** A plain world-px (scale-1) point — the space {@link Annotation.points} is stored in. */
export type DrawPoint = { x: number; y: number };

/**
 * The distance (world px, scale-1) a freehand drag must travel past its last KEPT sample
 * before a new one is recorded — `CELL`/4 (10px at the current 40px cell pitch). Small enough that a stroke still reads smooth once rendered with
 * tension (see {@link CombatStage}'s freehand render), large enough that a slow, jittery drag
 * doesn't pile up a sample every pixel (that's what {@link RDP_EPSILON}-on-release is for
 * regardless, but pre-thinning during the drag keeps the in-progress preview array itself
 * bounded, not just the final command).
 */
export const FREEHAND_SAMPLE_DIST = CELL / 4;

/**
 * The Ramer-Douglas-Peucker simplification tolerance (world px, scale-1) a finished freehand
 * stroke is reduced by on release — a point survives only if it deviates more than this
 * from the straight secant between its simplified
 * neighbours. 2px is tight enough that the simplified stroke still traces the drawn shape
 * faithfully at the surface's zoom range, loose enough to crush the redundant near-colinear
 * samples a real hand-drawn stroke is mostly made of.
 */
export const RDP_EPSILON = 2;

/** Mint an id for a freshly authored annotation (the same minting discipline as `objectAuthoring.ts`'s `objectGroupId`/`deploy.ts`'s `deployId`). */
export function mintAnnotationId(): AnnotationId {
  return typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `annot-${Math.random().toString(36).slice(2)}-${Date.now()}`;
}

/**
 * The distance (world px, scale-1) under which a freshly clicked polyline vertex is treated as
 * a repeat of the last accumulated one, rather than a new vertex — the finishing double-click's
 * TWO mousedowns land at ~the same board point, so without this the polyline would end with a
 * degenerate near-zero-length final segment.
 */
const POLYLINE_VERTEX_DEDUPE_DIST = 2;

/**
 * The click-vertex polyline's accumulator: append `point` as a new vertex, UNLESS it's within
 * {@link POLYLINE_VERTEX_DEDUPE_DIST} of the last one already accumulated (a repeat click — most
 * commonly the finishing double-click's second mousedown), in which case `vertices` is returned
 * unchanged. Pure + immutable (returns a new array on a real append) so {@link CombatStage} can
 * reassign its accumulator ref directly from the result. The first vertex (`vertices = []`) is
 * always kept — there's nothing yet to be a repeat of.
 */
export function accumulatePolylineVertex(vertices: DrawPoint[], point: DrawPoint): DrawPoint[] {
  const last = vertices[vertices.length - 1];
  if (last) {
    const dx = point.x - last.x;
    const dy = point.y - last.y;
    if (dx * dx + dy * dy < POLYLINE_VERTEX_DEDUPE_DIST * POLYLINE_VERTEX_DEDUPE_DIST) return vertices;
  }
  return [...vertices, point];
}

/**
 * Whether an in-progress freehand drag should record `point` as a new sample, given the last
 * one already kept (`undefined` for the very first sample — always kept). Pure distance-vs-
 * {@link FREEHAND_SAMPLE_DIST} threshold test — squared-distance to dodge a `sqrt` per pointer
 * move. The stage calls this per `onMouseMove` and only pushes into its sample ref when it
 * returns true, so the in-progress array never grows faster than the threshold allows.
 */
export function shouldSampleFreehandPoint(last: DrawPoint | undefined, point: DrawPoint, thresholdPx: number = FREEHAND_SAMPLE_DIST): boolean {
  if (!last) return true;
  const dx = point.x - last.x;
  const dy = point.y - last.y;
  return dx * dx + dy * dy >= thresholdPx * thresholdPx;
}

/** The perpendicular distance from `point` to the infinite line through `a`↔`b` (0 when `a === b`, the point-to-point distance then). */
function perpendicularDistance(point: DrawPoint, a: DrawPoint, b: DrawPoint): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const lenSq = dx * dx + dy * dy;
  if (lenSq === 0) {
    const ex = point.x - a.x;
    const ey = point.y - a.y;
    return Math.sqrt(ex * ex + ey * ey);
  }
  // |cross product| / |base| — twice the a-b-point triangle's area divided by its base length.
  const cross = Math.abs(dy * point.x - dx * point.y + b.x * a.y - b.y * a.x);
  return cross / Math.sqrt(lenSq);
}

/**
 * Ramer-Douglas-Peucker: reduce `points` to the subset that still traces the shape within
 * `epsilon` (world px, scale-1) — the endpoints are always kept, and an interior point survives
 * only if some kept segment's farthest deviating point exceeds `epsilon`. Iterative (an explicit
 * work-stack of `[start, end)` index spans, not recursive calls) so a long, jagged raw sample
 * list — the exact pathological case a real dense freehand drag produces — can't blow the call
 * stack; see `combatDrawing.test.ts`'s pathological-stroke case, which round-trips a
 * many-thousand-point input through this and asserts the result lands well under
 * `MAX_ANNOTATION_POINTS`. Fewer than 3 points has nothing to simplify (returned as-is).
 */
export function simplifyPolyline(points: DrawPoint[], epsilon: number = RDP_EPSILON): DrawPoint[] {
  if (points.length < 3) return points.slice();
  const keep = new Array<boolean>(points.length).fill(false);
  keep[0] = true;
  keep[points.length - 1] = true;
  const stack: [number, number][] = [[0, points.length - 1]];
  while (stack.length > 0) {
    const span = stack.pop()!;
    const start = span[0];
    const end = span[1];
    if (end <= start + 1) continue; // no interior points in this span
    let farIdx = -1;
    let farDist = epsilon;
    for (let i = start + 1; i < end; i++) {
      const d = perpendicularDistance(points[i]!, points[start]!, points[end]!);
      if (d > farDist) {
        farDist = d;
        farIdx = i;
      }
    }
    if (farIdx !== -1) {
      keep[farIdx] = true;
      stack.push([start, farIdx]);
      stack.push([farIdx, end]);
    }
  }
  return points.filter((_, i) => keep[i]);
}

/** Named arguments shared by {@link freehandAnnotationCommand}/{@link polylineAnnotationCommand}. */
type StrokeArgsBase = {
  /** An explicit id — omit to mint a fresh one ({@link mintAnnotationId}). Exposed for tests. */
  id?: AnnotationId;
  actorId: string;
  color: AnnotationColor;
};

/** Named arguments for {@link freehandAnnotationCommand}. */
export type FreehandStrokeArgs = StrokeArgsBase & {
  /** The raw sampled pointer points over the drag (world px, scale-1), press-first. */
  points: DrawPoint[];
  /** Override for {@link RDP_EPSILON} — exposed for tests, not threaded from the UI. */
  epsilon?: number;
};

/**
 * The finished freehand stroke's {@link AddAnnotationCommand} — or `null` when the simplified
 * result has fewer than 2 points (a click with no real drag draws nothing; a single point isn't
 * a visible stroke). Runs the raw `points` through {@link simplifyPolyline} at
 * {@link RDP_EPSILON} (or `args.epsilon`) — the ONE place a freehand drag's samples get reduced
 * before touching the command bus. `annotation.authorSlotId` is stamped to `args.actorId` — the
 * forge-proof shape the ownership gate expects; the caller never picks a different owner.
 */
export function freehandAnnotationCommand(args: FreehandStrokeArgs): AddAnnotationCommand | null {
  const points = simplifyPolyline(args.points, args.epsilon ?? RDP_EPSILON);
  if (points.length < 2) return null;
  const annotation: Annotation = {
    id: args.id ?? mintAnnotationId(),
    kind: "freehand",
    points,
    color: args.color,
    authorSlotId: args.actorId,
  };
  return { type: "add_annotation", actorId: args.actorId, annotation };
}

/** Named arguments for {@link polylineAnnotationCommand}. */
export type PolylineStrokeArgs = StrokeArgsBase & {
  /** The clicked vertices, verbatim (deliberate placement — never simplified), press-first. */
  points: DrawPoint[];
};

/**
 * The finished polyline's {@link AddAnnotationCommand} — or `null` when fewer than 2 vertices
 * were clicked (a single click with no second vertex draws nothing — {@link CombatStage}'s
 * finish gesture, e.g. Esc with one vertex down, should abandon rather than call this). Vertices
 * are stored **verbatim** — no RDP here, unlike freehand — deliberate placement is the whole
 * point of the click-vertex brush. Same `authorSlotId` stamping discipline as
 * {@link freehandAnnotationCommand}.
 */
export function polylineAnnotationCommand(args: PolylineStrokeArgs): AddAnnotationCommand | null {
  if (args.points.length < 2) return null;
  const annotation: Annotation = {
    id: args.id ?? mintAnnotationId(),
    kind: "polyline",
    points: args.points.slice(),
    color: args.color,
    authorSlotId: args.actorId,
  };
  return { type: "add_annotation", actorId: args.actorId, annotation };
}

/**
 * The widened hit region (world px, scale-1) a stroke's actual line accepts a click within. The
 * native-DOM + pure-math interaction model needs the width as an explicit constant — there's no
 * built-in widened-hit-region prop to lean on — and a thin stroke would otherwise be nearly
 * unclickable.
 */
export const ANNOTATION_HIT_STROKE_WIDTH = 16;

/**
 * How far from its path the drag eraser reaches, in world px: half {@link ANNOTATION_HIT_STROKE_WIDTH},
 * the width {@link annotationsAlongSegment} tests by default. The eraser's on-board ring is drawn at
 * this radius, so what the ring covers is what the drag erases.
 */
export const ERASER_HIT_RADIUS = ANNOTATION_HIT_STROKE_WIDTH / 2;

/** The perpendicular distance from `point` to the SEGMENT `a`↔`b` (clamped to the segment, unlike
 *  `perpendicularDistance`'s infinite-line test above — a click past a stroke's endpoint must not
 *  register against the line the endpoint would extend to). */
function distanceToSegment(point: DrawPoint, a: DrawPoint, b: DrawPoint): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const lenSq = dx * dx + dy * dy;
  if (lenSq === 0) return Math.hypot(point.x - a.x, point.y - a.y);
  const t = Math.max(0, Math.min(1, ((point.x - a.x) * dx + (point.y - a.y) * dy) / lenSq));
  const px = a.x + t * dx;
  const py = a.y + t * dy;
  return Math.hypot(point.x - px, point.y - py);
}

/**
 * The topmost annotation whose stroke passes within {@link ANNOTATION_HIT_STROKE_WIDTH}/2 of `world`
 * (world px, scale-1), or `null`. The native-DOM + pure-math twin of {@link hitTestToken} in
 * `combatCells.ts`. `annotations` is a record (insertion order), so the LAST-inserted match wins on overlap
 * — the most recently drawn stroke reads as "on top," mirroring the render z-order.
 */
export function hitTestAnnotation(
  annotations: Record<AnnotationId, Annotation>,
  world: DrawPoint,
  hitWidth: number = ANNOTATION_HIT_STROKE_WIDTH,
): AnnotationId | null {
  const half = hitWidth / 2;
  let hit: AnnotationId | null = null;
  for (const a of Object.values(annotations)) {
    for (let i = 0; i < a.points.length - 1; i++) {
      if (distanceToSegment(world, a.points[i]!, a.points[i + 1]!) <= half) {
        hit = a.id;
        break;
      }
    }
  }
  return hit;
}

/** Twice the signed area of triangle `(o, a, b)` — positive for a CCW turn `o→a→b`, negative for
 *  CW, zero for collinear. The orientation primitive {@link segmentsIntersect} is built on. */
function cross2(o: DrawPoint, a: DrawPoint, b: DrawPoint): number {
  return (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x);
}

/** Whether `p` lies within segment `a`↔`b`'s axis-aligned bounding box (inclusive, tiny epsilon for
 *  float slop) — only meaningful once the caller already knows `p` is COLLINEAR with `a`↔`b`
 *  ({@link segmentsIntersect}'s degenerate/collinear-overlap cases), so this alone is not a
 *  point-on-segment test. */
function onSegmentBox(p: DrawPoint, a: DrawPoint, b: DrawPoint): boolean {
  const eps = 1e-9;
  return (
    Math.min(a.x, b.x) - eps <= p.x &&
    p.x <= Math.max(a.x, b.x) + eps &&
    Math.min(a.y, b.y) - eps <= p.y &&
    p.y <= Math.max(a.y, b.y) + eps
  );
}

/**
 * A straight segment between two {@link DrawPoint}s — the shared shape {@link segmentsIntersect},
 * {@link segmentDistance}, and {@link annotationsAlongSegment} pass around instead of four loose
 * point parameters, which would trip the repo's gated `max-params: 3` lint rule. `from`/`to` name
 * the eraser's OWN travel segment's direction; a stroke's own segment (`a.points[i]` → `a.points[i +
 * 1]`) is wrapped in the same shape purely for the shared math, with no directional meaning of
 * its own.
 */
export type DrawSegment = { from: DrawPoint; to: DrawPoint };

/**
 * The textbook orientation-based 2D segment-intersection test — does `s1` touch `s2` anywhere (a
 * proper crossing, an endpoint landing on the other segment, or a collinear overlap)?
 * {@link segmentDistance} needs this because point-to-segment distance alone (composed over just
 * the four endpoints) cannot detect an X-style crossing — see that function's doc comment.
 */
function segmentsIntersect(s1: DrawSegment, s2: DrawSegment): boolean {
  const { from: a1, to: a2 } = s1;
  const { from: b1, to: b2 } = s2;
  const d1 = cross2(b1, b2, a1);
  const d2 = cross2(b1, b2, a2);
  const d3 = cross2(a1, a2, b1);
  const d4 = cross2(a1, a2, b2);
  if (((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0)) && ((d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0))) return true;
  if (d1 === 0 && onSegmentBox(a1, b1, b2)) return true;
  if (d2 === 0 && onSegmentBox(a2, b1, b2)) return true;
  if (d3 === 0 && onSegmentBox(b1, a1, a2)) return true;
  if (d4 === 0 && onSegmentBox(b2, a1, a2)) return true;
  return false;
}

/**
 * The minimum distance between segment `s1` and segment `s2` — TRUE segment-to-segment distance,
 * deliberately NOT the naive "compose {@link distanceToSegment} over the four endpoint pairs"
 * shortcut. That shortcut is wrong for a crossing pair: two segments that cross like an X can have
 * BOTH pairs of endpoints far from the other segment while the segments themselves touch at an
 * interior point (e.g. `s1: (0,0)→(10,10)`, `s2: (0,10)→(10,0)`, crossing at `(5,5)` — every
 * endpoint is `√50 ≈ 7px` from the opposite segment, so a four-endpoint composition reports ~7px
 * when the true answer is 0). That is exactly the fast-drag case this exists for: one `mousemove`
 * sample to the next can cross straight through a stroke's segment, and a point-only or
 * naively-composed test would silently miss it.
 *
 * So this explicitly tests for an intersection first ({@link segmentsIntersect} — a proper crossing,
 * a touching endpoint, or a collinear overlap all count, distance 0), and only falls back to the
 * four-endpoint minimum when the segments do NOT intersect — which IS the correct answer in that
 * case (a standard 2D result: for two non-intersecting segments, the closest pair of points always
 * includes at least one segment's own endpoint).
 *
 * **Stated limitation:** this is exact for one straight segment pair, which is exactly what one
 * `mousemove` sample (`s1`, the eraser's travel) against one stroke segment (`s2`, `a[i]`→`a[i+1]`)
 * is. It does not by itself make a whole multi-segment stroke's hit-test "continuous" across
 * MULTIPLE eraser samples — {@link annotationsAlongSegment} is called once per `mousemove`, each
 * call covering only that one sample's travel; a stroke is caught as soon as any single travel
 * segment comes within `hitWidth`/2 of any single stroke segment, which is the all-hits semantics
 * the drag eraser wants.
 */
function segmentDistance(s1: DrawSegment, s2: DrawSegment): number {
  if (segmentsIntersect(s1, s2)) return 0;
  return Math.min(
    distanceToSegment(s1.from, s2.from, s2.to),
    distanceToSegment(s1.to, s2.from, s2.to),
    distanceToSegment(s2.from, s1.from, s1.to),
    distanceToSegment(s2.to, s1.from, s1.to),
  );
}

/**
 * ALL annotation ids whose stroke passes within `hitWidth`/2 of the eraser's TRAVELED segment
 * `travel` — the drag-eraser's all-hits counterpart to {@link hitTestAnnotation}'s topmost-only
 * select hit-test (deliberately a separate function, not a parameter on that one, because the
 * select gesture's topmost-wins behavior must not change). Walks every stroke's own consecutive
 * segment pairs and tests each against the travel segment with {@link segmentDistance} (true
 * segment-to-segment distance — see its doc comment for why a naive point-only or endpoint-only
 * test would silently skip a stroke the eraser dragged straight through between two samples).
 * Measured at 1.358ms per eraser sample against an absurd 1000-stroke × 256-point corpus — hence
 * no spatial index, no throttle, no incremental structure.
 * Return order follows `annotations`' own iteration (insertion) order — irrelevant to the caller,
 * which only cares about set membership; not a z-order like {@link hitTestAnnotation}'s.
 */
export function annotationsAlongSegment(
  annotations: Record<AnnotationId, Annotation>,
  travel: DrawSegment,
  hitWidth: number = ANNOTATION_HIT_STROKE_WIDTH,
): AnnotationId[] {
  const half = hitWidth / 2;
  const hits: AnnotationId[] = [];
  for (const a of Object.values(annotations)) {
    for (let i = 0; i < a.points.length - 1; i++) {
      if (segmentDistance(travel, { from: a.points[i]!, to: a.points[i + 1]! }) <= half) {
        hits.push(a.id);
        break;
      }
    }
  }
  return hits;
}

/**
 * The select gesture's **recolor** command — a wholesale {@link SetAnnotationCommand} replacing
 * `annotation` with only its `color` changed, everything else (id/kind/points/`authorSlotId`)
 * carried through verbatim. `args.actorId` is the ISSUING client's actor — NOT necessarily
 * `annotation.authorSlotId` (a GM may recolor any stroke); the gate (`canIssueEncounter`) checks
 * `state.annotations[id].authorSlotId === actorId` OR GM, exactly like
 * {@link removeAnnotationCommand}, so an unauthorized recolor is
 * rejected server-side regardless of what this builder hands it — the UI's owner/GM check that
 * decides whether to even OFFER the swatches is a separate affordance, not this function's job.
 */
export function recolorAnnotationCommand(args: { actorId: string; annotation: Annotation; color: AnnotationColor }): SetAnnotationCommand {
  return { type: "set_annotation", actorId: args.actorId, annotation: { ...args.annotation, color: args.color } };
}

/**
 * The select gesture's **delete** command — by id, no
 * annotation payload needed (the reducer looks up the existing one to gate + build the inverse).
 * Deliberately takes only `annotationId`, not the full `Annotation`: the Delete/Backspace key
 * (and the tiny inspector's delete control) fire this for WHATEVER is currently selected,
 * regardless of who authored it — same non-gating discipline as {@link recolorAnnotationCommand},
 * the server wall is what actually enforces ownership.
 */
export function removeAnnotationCommand(args: { actorId: string; annotationId: AnnotationId }): RemoveAnnotationCommand {
  return { type: "remove_annotation", actorId: args.actorId, annotationId: args.annotationId };
}

/**
 * The drag eraser's finish command — ONE `batch` of `remove_annotation` leaves,
 * one per id in `args.annotationIds`, each stamped with `args.actorId`, so a whole erase drag undoes
 * as a SINGLE unit — the same batch-as-one-undo-unit house pattern `combatAuthoring.ts`'s
 * stroke/wall/room brushes already use (never dispatch N leaf `remove_annotation`s, or undo unwinds
 * the drag one stroke at a time). Returns `null` for zero ids rather than an empty batch — an empty
 * batch is still a dispatchable command, and the caller (an erase drag that touched nothing) should
 * simply not dispatch, mirroring {@link freehandAnnotationCommand}/{@link polylineAnnotationCommand}
 * returning `null` for a degenerate stroke. `args.annotationIds` is NOT filtered for
 * ownership/existence here — the caller (`CombatStage`) is responsible for both the erasable
 * filter and dropping any id that's gone stale since it was collected; this builder just turns a
 * trusted id list into the command shape.
 */
export function eraseAnnotationsCommand(args: { actorId: string; annotationIds: AnnotationId[] }): EncounterBatchCommand | null {
  if (args.annotationIds.length === 0) return null;
  const commands: RemoveAnnotationCommand[] = args.annotationIds.map((annotationId) => ({
    type: "remove_annotation",
    actorId: args.actorId,
    annotationId,
  }));
  return { type: "batch", commands };
}
